<template>
    <div>
        <div class="heading">Privacy Policy</div>
        <p>Effective Date: August 3rd, 2026</p>
       <p>{{company_name}} is committed to providing quality services to you and this policy outlines our ongoing obligations to you in respect of how we manage your Personal Information.</p>
<p>We have adopted Malawi’s Data Protection Act, 2023, which provides for the protection of personal information, regulation of data processing, the safe movement of personal information, the strict obligations of entities like {{company_name}}, and the legal rights of data subjects (the individuals to whom the personal information belongs).</p>

<div class="policy-heading">WHAT IS PERSONAL DATA, WHY DO WE COLLECT IT?</div>

<p>Personal Data is any information relating to an identifiable natural person who can be directly or indirectly identified, particularly by reference to an identifier such as a name, contact details, or online identifiers.</p>
<p>The specific personal data we collect on our platform includes:</p>
<ul>
  <li class="policy-item">Full names</li>
  <li class="policy-item">Email Addresses</li>
  <li class="policy-item">Phone numbers</li>
  <li class="policy-item">Social Media profiles/links</li>
  <li class="policy-item">Personal Website URLs</li>
  <li class="policy-item">Job Titles</li>
</ul>
<p>This Personal Data is obtained in several ways, primarily through user registration and inputs from Vocation’s CV Builder (which collects personal summaries, work and education history, achievements, and certifications).</p>
<p>We collect and process your Personal Data based strictly on your explicit consent for the primary purpose of providing our recruitment and job-matching services to you. We may also use your data for secondary purposes closely related to the primary purpose, in circumstances where you would reasonably expect such use or disclosure (such as sending relevant job alerts or system updates).</p>

<div class="policy-heading">DISCLOSURE OF PERSONAL DATA</div>

<p>We do not sell, rent, or trade your Personal Data. However, your Personal Data may be disclosed to third parties in a limited number of circumstances, including:</p>
<ul>
  <li class="policy-item">Employers and Recruiters: When you create a profile, apply for a job, or use Vocation’s CV Builder, your data will be shared with employers and recruiters on the platform in accordance with your visibility settings and explicit consent.</li>
  <li class="policy-item">Service Providers: We may share data with trusted third-party service providers who assist us in operating our platform, hosting our databases, or delivering services to you (such as secure cloud hosting providers or analytics platforms), provided they are bound by strict data confidentiality agreements.</li>
  <li class="policy-item">Legal Requirements: Where we are required or authorized to do so by written law, a regulatory body (such as MACRA), or an order of a court of law.</li>
  <li class="policy-item">Explicit Consent: To any other third party where you have provided explicit, informed consent for the disclosure.</li>
</ul>

<div class="policy-heading">CROSS-BORDER DATA TRANSFERS</div>

<p>To deliver a highly stable, secure, and available job-matching infrastructure, {{company_name}} may host, store, and process your Personal Data on secure cloud servers located outside of Malawi.</p>
<p>When transferring your data across international borders, we strictly comply with Part VII of Malawi’s Data Protection Act, ensuring that your data remains protected regardless of its physical location. We achieve this by:</p>
<ul>
  <li class="policy-item">Adequacy Approvals: Verifying that the recipient country provides an adequate level of data protection that matches or exceeds Malawian legal standards.</li>
  <li class="policy-item">Contractual Safeguards: Utilizing binding corporate rules, standard data protection clauses, and strict data processing agreements with our cloud infrastructure providers (such as database hosting and API servers) to contractually guarantee confidentiality, integrity, and compliance.</li>
  <li class="policy-item">Functional Necessity: Limiting cross-border data movement strictly to what is required to execute our core contractual platform features—such as securely matching your profile data with employers and processing system queries.</li>
</ul>

<div class="policy-heading">SECURITY AND RETENTION OF PERSONAL DATA</div>

<p>{{company_name}} implements robust technical and organizational measures to ensure your Personal Data is stored securely and protected against misuse, loss, unauthorized access, modification, or disclosure.</p>
<p>In alignment with the Storage Limitation principles of Malawi's Data Protection Act, we do not retain personal data longer than necessary to fulfill our job-matching and recruitment service objectives. Our automated data lifecycle policies operate as follows:</p>
<ul>
  <li class="policy-item">Deactivation and Reactivation Window: If you explicitly deactivate your account or it becomes entirely stagnant, the platform initiates a temporary 90-day grace period. During these 90 days, your profile remains dormant, but you can fully reactivate it at any time simply by logging back into the platform.</li>
  <li class="policy-item">Anonymization and Erasure: Once the 90-day threshold passes without account activity, your account configuration triggers an automated routine. All personally identifying details—including full names, precise contact records, and custom resume fields—are permanently anonymized and destroyed.</li>
</ul>
<p>Technical Compliance Note: Fully anonymized structural data ceases to be classified as "personal data" under the law. We retain only desensitized, anonymous structural metrics for platform performance auditing and analytical reporting, ensuring complete user privacy while maintaining data integrity.</p>

<div class="policy-heading">TREATMENT OF DORMANT AND INACTIVE ACCOUNTS</div>

<p>To ensure we do not retain your information longer than necessary, {{company_name}} actively monitors account activity. If your account remains completely dormant (no login activity) for a continuous period of 12 months, it will be flagged for protective cleanup:</p>
<ul>
  <li class="policy-item">Notification Notice: Before taking any action, we will send an automated notice to your registered email address, giving you thirty (30) days to preserve your account simply by logging in.</li>
  <li class="policy-item">Automated Data Lifecycle: If you do not log in within that window, the account will be placed into our 90-day deactivation grace period and subsequently permanently anonymized and destroyed in accordance with our standard data protection routines.</li>
</ul>

<div class="policy-heading">TREATMENT OF SUSPENDED AND TERMINATED ACCOUNTS</div>

<p>{{company_name}} reserves the right to suspend or terminate access to accounts that violate our Terms of Service, engage in fraudulent activity, or compromise platform security.</p>
<ul>
  <li class="policy-item">Exemption from Standard Erasure: In compliance with data safety standards and our legitimate interests in preventing platform abuse, accounts suspended for security, safety, or fraudulent violations are exempt from our automated inactivity erasure and anonymization lifecycles.</li>
  <li class="policy-item">Retention for Security: We retain essential identifying indicators and system audit logs associated with suspended accounts for an extended period. This data is strictly restricted from public visibility and is maintained solely to enforce structural platform bans, prevent malicious re-registration, protect our community, and comply with potential law enforcement or judicial requests under Malawian law.</li>
</ul>

<div class="policy-heading">POLICY UPDATES</div>

<p>This Privacy Policy may change from time to time to mirror platform feature upgrades or changing legal frameworks. The most current version will always be available on our website at vocation.mw/privacy-policy.</p>
<p>If we implement material updates that structurally change how we collect, process, or store your Personal Data, we will notify you actively via your registered email address or through a prominent system notification inside the Vocation platform dashboard before the changes take effect.</p>

<div class="policy-heading">PRIVACY POLICY COMPLAINTS AND ENQUIRIES</div>

<p>If you have any questions, queries, data access requests, or complaints regarding how {{company_name}} manages your Personal Data, please contact us directly via our dedicated support channel:</p>
<ul>
  <li class="policy-item">Email: privacy@vocation.mw</li>
</ul>
<p>We take all privacy inquiries seriously and will investigate and respond to your request within thirty (30) days.</p>

<div class="policy-heading">ESCALATION TO THE DATA PROTECTION AUTHORITY</div>

<p>If you are located in Malawi and believe that your inquiry or complaint has not been satisfactorily resolved by our internal privacy team, you have the legal right under the Data Protection Act to lodge a formal complaint directly with the national regulatory authority:</p>
<ul>
  <li class="policy-item">Authority: The Data Protection Authority (Malawi Communications Regulatory Authority - MACRA)</li>
  <li class="policy-item">Address: 1st Floor, Green Heritage House, 2 Khonje Close, City Centre, P.O. Box 30214, Lilongwe, Malawi</li>
  <li class="policy-item">Email: info@dpa.mw / dg@macra.mw</li>
  <li class="policy-item">Web Portal: www.dpa.mw</li>
</ul>
    </div>
</template>
<script>
export default {
       data(){
        return{
            company_name: process.env.VUE_APP_OFFICIAL_COMPANY_NAME,
            privacy_email: process.env.VUE_APP_PRVACY_EMAIL
        }
    }
}
</script>
<style scoped>
        .heading, p, li{
        font-family: var(--body-font);
    }


    .heading{
        margin-top:-1rem;
        color:var(--intro);
        margin-bottom:1.2rem;
        font-size:3.2rem;
        font-family: var(--page-header-font);
         
    }

    .policy-heading{
        color:var(--intro);
        margin-bottom:0.9rem;
        font-size:1.5rem;
    }

    @media(max-width: 768px){
        .heading{
          margin-top:0rem;
            font-size:2rem;
             margin-bottom:0.8rem;
        }

        .policy-heading{
          font-size:1rem;
        }

        p{
          font-size:0.9rem;
        }
    }
</style>